import { useState } from "react";
import { Avatar, Dropdown, Popover, Modal, Layout } from "../ui";
import { FaUser, FaSignOutAlt, FaCog } from "react-icons/fa";
import { ChevronDown, LogOut, Moon, Sun, Bell } from "lucide-react";
import { UPLOADS_URL, AUTH } from "../../config/constants";
import { useSelector, useDispatch } from "react-redux";
import { Get } from "../../config/api/get";
import { useNavigate } from "react-router-dom";
import { removeUser } from "../../redux/slice/authSlice";
import { resetNotifications } from "../../redux/slice/notificationSlice";
import { useTheme } from "../../hooks/useTheme";
import { useNotificationSocket } from "../../hooks/useNotificationSocket";
import { disconnectAppSocket } from "../../config/socket";
import { useSupportSocket } from "../../hooks/useSupportSocket";
import {
  useToggleNotificationRead,
} from "../../query/notificationQuery";
import { snippet, relativeCreatedAt } from "../../views/notifications/notificationHelpers";
import "../../views/notifications/notifications.css";

const { Header } = Layout;

const ClientHeader = ({
  title = "Dashboard",
  isMobile = false,
  menuOpen = false,
  onMenuToggle,
}) => {
  const latestNotifications = useSelector(
    (state) => state.notification.latestNotifications
  );
  const notificationsCount = useSelector((state) => state.notification.count);
  const [logoutModal, setLogoutModal] = useState(false);
  const user = useSelector((state) => state.user.userData);
  const token = useSelector((state) => state.user.userToken);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isDark, toggleTheme } = useTheme();

  const firstName = user?.firstName || user?.fatherFirstName || "Admin";
  const lastName = user?.lastName || user?.fatherLastName || "";
  const displayName = `${firstName} ${lastName}`.trim();
  const avatarSrc = user?.image ? `${UPLOADS_URL}/${user.image}` : undefined;
  const initials =
    `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || "A";

  useNotificationSocket(token);
  useSupportSocket(token);
  const { mutate: toggleRead } = useToggleNotificationRead();

  const clearSession = () => {
    disconnectAppSocket();
    dispatch(removeUser());
    dispatch(resetNotifications());
    navigate("/signin", { replace: true });
  };

  const logout = async () => {
    setLogoutModal(false);
    try {
      await Get(AUTH.logout, token);
    } catch {
      // Still clear local session if the API call fails
    }
    clearSession();
  };

  const items = [
    {
      key: "1",
      label: (
        <div
          className="headerDropdown"
          style={{
            fontSize: "14px",
            display: "flex",
            alignItems: "center",
            padding: "5px 12px",
          }}
        >
          <FaUser style={{ fontSize: "14px" }} /> &nbsp; My Profile
        </div>
      ),
      onClick: () => navigate("/profile"),
    },
    {
      key: "settings",
      label: (
        <div
          className="headerDropdown"
          style={{
            fontSize: "14px",
            display: "flex",
            alignItems: "center",
            padding: "5px 12px",
          }}
        >
          <FaCog style={{ fontSize: "14px" }} /> &nbsp; Settings
        </div>
      ),
      onClick: () => navigate("/settings"),
    },
    {
      key: "2",
      label: (
        <div
          style={{
            fontSize: "14px",
            display: "flex",
            alignItems: "center",
            padding: "5px 12px",
          }}
        >
          <FaSignOutAlt style={{ fontSize: "12px" }} />
          &nbsp; Logout
        </div>
      ),
      onClick: () => {
        window.setTimeout(() => setLogoutModal(true), 0);
      },
    },
  ];

  const content = (
    <div className="header-popover">
      <div className="header-popover-head">
        <h3>Notifications</h3>
        {notificationsCount > 0 ? (
          <span className="header-popover-count is-unread">{notificationsCount}</span>
        ) : null}
      </div>
      <div className="header-popover-list">
        {latestNotifications && latestNotifications.length > 0 ? (
          latestNotifications.map((item) => (
            <button
              key={item._id || item.title}
              type="button"
              className={`header-popover-item${item.isRead ? "" : " is-unread"}`}
              onClick={() => {
                if (!item.isRead) toggleRead({ id: item._id, isRead: true });
                navigate("/inbox");
              }}
            >
              <span className="header-popover-icon" aria-hidden>
                <Bell size={13} />
              </span>
              <span className="notif-popover-copy">
                <strong>{item.title}</strong>
                <em>{snippet(item.content, 72)}</em>
                <span className="notif-popover-meta">
                  {relativeCreatedAt(item.createdAt) || "Just now"}
                </span>
              </span>
            </button>
          ))
        ) : (
          <div className="header-popover-empty">No new notifications</div>
        )}
      </div>
      <button type="button" className="header-popover-footer" onClick={() => navigate("/inbox")}>
        View all
      </button>
    </div>
  );

  return (
    <>
      <Header className={`admin-header${isMobile ? " is-mobile-header" : ""}`}>
        <div className="admin-header-inner">
          {isMobile ? (
            <div className="m-topbar">
              <div className="m-topbar-brand">
                <button
                  type="button"
                  className={`m-topbar-mark is-tree${menuOpen ? " is-open" : ""}`}
                  aria-label={menuOpen ? "Close menu" : "Open menu"}
                  aria-expanded={menuOpen}
                  aria-controls="admin-mobile-drawer"
                  onClick={onMenuToggle}
                >
                  <img src="/images/splash_tree_icon.png" alt="" />
                </button>
                <div className="m-topbar-copy">
                  <p className="m-topbar-school">Birchwood</p>
                  <h1 className="admin-page-title">{title}</h1>
                </div>
              </div>
              <div className="admin-header-side m-topbar-actions">
                <Popover
                  content={content}
                  placement="bottomRight"
                  arrow={false}
                  trigger="click"
                  className="headerPopover"
                >
                  <button type="button" className="admin-icon-btn" aria-label="Notifications">
                    <Bell size={14} />
                    {notificationsCount > 0 && <span className="admin-icon-badge" />}
                  </button>
                </Popover>

                <button
                  type="button"
                  className="admin-icon-btn"
                  aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
                  onClick={toggleTheme}
                >
                  {isDark ? <Sun size={14} /> : <Moon size={14} />}
                </button>

                <Dropdown menu={{ items }} trigger={["click"]} placement="bottomRight">
                  <button type="button" className="admin-profile is-mobile-avatar" aria-label="Account menu">
                    <span className="admin-profile-avatar">
                      <Avatar size={32} src={avatarSrc}>
                        <span className="admin-profile-fallback">{initials}</span>
                      </Avatar>
                    </span>
                  </button>
                </Dropdown>
              </div>
            </div>
          ) : (
            <>
              <div className="admin-header-main">
                <div className="admin-header-title-wrap">
                  <h1 className="admin-page-title">{title}</h1>
                </div>
              </div>

              <div className="admin-header-side">
                <Popover
                  content={content}
                  placement="bottomRight"
                  arrow={false}
                  trigger="click"
                  className="headerPopover"
                >
                  <button type="button" className="admin-icon-btn" aria-label="Notifications">
                    <Bell size={16} />
                    {notificationsCount > 0 && <span className="admin-icon-badge" />}
                  </button>
                </Popover>

                <button
                  type="button"
                  className="admin-icon-btn"
                  aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
                  onClick={toggleTheme}
                >
                  {isDark ? <Sun size={16} /> : <Moon size={16} />}
                </button>

                <Dropdown menu={{ items }} trigger={["click"]} placement="bottomRight">
                  <button type="button" className="admin-profile" aria-label="Account menu">
                    <span className="admin-profile-avatar">
                      <Avatar size={36} src={avatarSrc}>
                        <span className="admin-profile-fallback">{initials}</span>
                      </Avatar>
                    </span>
                    <span className="admin-user-meta">
                      <span className="admin-user-name">{displayName}</span>
                      <span className="admin-user-role">Admin</span>
                    </span>
                    <ChevronDown className="admin-profile-caret" size={14} strokeWidth={2.2} />
                  </button>
                </Dropdown>
              </div>
            </>
          )}
        </div>
      </Header>

      <Modal
        open={logoutModal}
        onCancel={() => setLogoutModal(false)}
        footer={null}
        className="logout-modal"
      >
        <div className="logout-modal-inner">
          <div className="logout-modal-icon">
            <LogOut size={28} />
          </div>
          <h2>Log out?</h2>
          <p>Are you sure you want to log out of Birchwood Academy?</p>
        </div>
        <div className="logout-modal-actions">
          <button
            type="button"
            className="logout-modal-cancel"
            onClick={() => setLogoutModal(false)}
          >
            Cancel
          </button>
          <button type="button" className="logout-modal-confirm" onClick={logout}>
            Log out
          </button>
        </div>
      </Modal>
    </>
  );
};

export default ClientHeader;
